import { css } from '@emotion/css';
import { useEffect, useRef, type JSX } from 'react';
import createREGL from 'regl';
import fragShader from './shaders/fragment.glsl?raw';
import vertShader from './shaders/vertex.glsl?raw';

const canvasStyle = css`
  top: 0;
  left: 0;
  position: absolute;
  width: 100%;
  height: 100%;
`;

interface ShaderImageProps {
  imageBlob: Blob | null;
}

export function ShaderImage({ imageBlob }: ShaderImageProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (!canvasRef.current || !imageBlob) return;

    let frameLoop: createREGL.Cancellable | undefined;
    const regl = createREGL(canvasRef.current);

    const img = new Image();
    const imageUrl = URL.createObjectURL(imageBlob);
    img.src = imageUrl;

    img.onload = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      const drawCommand = regl({
        frag: fragShader,
        vert: vertShader,
        attributes: {
          a_position: [
            [0, 0],
            [0, 2],
            [2, 0],
          ],
        },
        uniforms: {
          u_texture: regl.texture(img),
          u_time: ({ time }) => time,
        },
        count: 3,
      });

      frameLoop = regl.frame(() => {
        regl.clear({ color: [0, 0, 0, 1] });
        drawCommand();
      });
    };

    return () => {
      if (frameLoop) {
        frameLoop.cancel();
      }
      regl.destroy();
      URL.revokeObjectURL(imageUrl);
    };
  }, [imageBlob]);

  return <canvas ref={canvasRef} className={canvasStyle} />;
}
